import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  extractPoseData,
  getPoseData3D,
  getPoseDataWithRetry,
  getDisplayPose3DWithRetry,
} from '@/services/videoApi';
import type { EvaluationResult, RawPoseFrame } from '@/types/api';
export { mirrorLandmarks } from '@/utils/poseUtils';

/**
 * Hook to extract pose data from a video
 */
export function usePoseExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: extractPoseData,
    onSuccess: (data) => {
      // Cache the pose data for later use
      queryClient.setQueryData(['pose-data', data.video_id], data.pose_data);
    },
  });
}

/**
 * Hook to get cached pose data (relies on mutation)
 */
export function usePoseData(videoId: string | undefined) {
  return useQuery({
    queryKey: ['pose-data', videoId],
    queryFn: () => {
      // This will only be called if data isn't already cached
      throw new Error('Pose data not extracted yet');
    },
    enabled: false, // Don't auto-fetch, rely on mutation to populate cache
  });
}

/**
 * Hook to fetch pose data from API (auto-fetches if available).
 *
 * Uses getPoseDataWithRetry so that a 202 Accepted response — returned by the
 * backend when pose extraction is scheduled but not yet complete — is handled
 * transparently: the function polls up to 30 times respecting the Retry-After
 * header before resolving (or throwing on persistent failure).
 *
 * Returns pose data array where each element is a frame with landmarks.
 */
export function useFetchPoseData(videoId: string | undefined) {
  return useQuery({
    queryKey: ['pose-data-fetch', videoId],
    queryFn: async () => {
      if (!videoId) throw new Error('No video ID provided');
      return getPoseDataWithRetry(videoId);
    },
    enabled: !!videoId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Retry logic is handled inside getPoseDataWithRetry
  });
}

/**
 * Hook to fetch pose data including 3D lifted landmarks from the API.
 * Returns frames (2D), landmarks_3d (H36M 17-joint, T×17×3), and model info.
 * Silently returns null fields when the backend has no 3D data for the video.
 */
export function useFetchPoseData3D(videoId: string | undefined) {
  return useQuery({
    queryKey: ['pose-data-3d', videoId],
    queryFn: async () => {
      if (!videoId) throw new Error('No video ID');
      return getPoseData3D(videoId);
    },
    enabled: !!videoId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook to fetch anthropometric display-quality 3D pose data for the 3D viewer.
 *
 * Uses the /display-3d endpoint, which computes depth directly from the 2D
 * pose cache via Drillis-Contini bone-length priors + per-bone foreshortening
 * (anthropometric_skeleton.compute_display_3d()) — NOT a lifted/VideoPose3D
 * output; the lifting stack (VideoPose3D, PoseMamba, GVHMR) was deleted by
 * the 3D retirement (CLAUDE.md § "3D is RETIRED from scoring — visual-only").
 * This 3D is a permitted, scoring-independent VISUAL effect only — scoring
 * continues to use useFetchPoseData3D / getPoseData3D unchanged (2D).
 *
 * Uses getDisplayPose3DWithRetry so that a 202 Accepted response — returned by the
 * backend when the display-3d cache is being populated — is polled transparently.
 *
 * Returns null silently when the endpoint fails (no pose cache, video not found).
 * staleTime: Infinity because display-3d data is computed once and never changes.
 */
export function useDisplayPose3D(videoId: string | undefined) {
  return useQuery({
    queryKey: ['display-3d', videoId],
    queryFn: async () => {
      if (!videoId) return null;
      return getDisplayPose3DWithRetry(videoId);
    },
    enabled: !!videoId,
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Extract 3D landmarks from evaluation metadata when available.
 * Returns null when 3D data is not present.
 *
 * This is a passthrough utility — 3D landmarks are not yet used by
 * overlays but are available for future 3D visualization features.
 */
export function extractLandmarks3DFromMetadata(
  metadata: EvaluationResult['metadata']
): {
  landmarks_3d: number[][][] | null;  // (T, 17, 3)
  model: string | null;
  isGravityAligned: boolean | null;
} {
  if (!metadata || !metadata['3d_lifting_enabled']) {
    return { landmarks_3d: null, model: null, isGravityAligned: null };
  }

  return {
    landmarks_3d: metadata.landmarks_3d ?? null,
    model: metadata['3d_model'] ?? null,
    isGravityAligned: metadata['3d_gravity_aligned'] ?? null,
  };
}

/**
 * Extract landmarks array from a pose frame
 * Returns normalized {x, y, visibility} array for use in overlays
 */
export function extractLandmarksFromFrame(
  poseData: RawPoseFrame[] | undefined,
  frameIndex: number
): Array<{ x: number; y: number; visibility?: number }> | undefined {
  if (!poseData || frameIndex < 0 || frameIndex >= poseData.length) {
    return undefined;
  }

  const frame = poseData[frameIndex];
  if (!frame?.landmarks || !Array.isArray(frame.landmarks)) {
    return undefined;
  }

  // Landmarks are already in {x, y, z, visibility} format
  return frame.landmarks.map((lm) => ({
    x: lm.x,
    y: lm.y,
    visibility: lm.visibility,
  }));
}

